import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { CrewMember } from "@/mocks/admin/crew";

type DayKey = "M" | "T" | "W" | "Th" | "F" | "Sa" | "Su";

const DAYS: { key: DayKey; full: string; short: string }[] = [
  { key: "M", full: "Monday", short: "Mon" },
  { key: "T", full: "Tuesday", short: "Tue" },
  { key: "W", full: "Wednesday", short: "Wed" },
  { key: "Th", full: "Thursday", short: "Thu" },
  { key: "F", full: "Friday", short: "Fri" },
  { key: "Sa", full: "Saturday", short: "Sat" },
  { key: "Su", full: "Sunday", short: "Sun" },
];

type Props = {
  member: CrewMember;
  onUpdated: () => Promise<void> | void;
  onToast: (msg: string) => void;
};

export default function AvailabilityTab({ member, onUpdated, onToast }: Props) {
  const [selected, setSelected] = useState<DayKey[]>(member.availability);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setSelected(member.availability);
    setDirty(false);
  }, [member.id, member.availability]);

  const toggle = (key: DayKey) => {
    setSelected((prev) => {
      const next = prev.includes(key) ? prev.filter((d) => d !== key) : [...prev, key];
      setDirty(true);
      return next;
    });
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    const { error } = await supabase
      .from("crew_members")
      .update({ availability: selected })
      .eq("id", member.id);
    if (error) onToast(error.message);
    else {
      onToast("Availability updated");
      setDirty(false);
      await onUpdated();
    }
    setSaving(false);
  };

  // 2-week look-ahead with monday as first day; today highlight
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const twoWeeks = Array.from({ length: 14 }).map((_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });

  const dayKeyForDate = (d: Date): DayKey => {
    // 0 Sun .. 6 Sat -> map to our keys
    const map: DayKey[] = ["Su", "M", "T", "W", "Th", "F", "Sa"];
    return map[d.getDay()];
  };

  const isToday = (d: Date) => d.toDateString() === today.toDateString();

  return (
    <div className="px-5 py-5 space-y-5">
      <div>
        <h2 className="font-display text-xl font-bold text-ink-900">My Availability</h2>
        <p className="text-[11px] text-ink-500 mt-0.5">
          Tap the days you can work. Dispatch uses this to assign jobs.
        </p>
      </div>

      {/* Weekly toggles */}
      <section className="bg-white border border-ink-100 rounded-2xl p-4">
        <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold mb-3">
          Recurring weekly
        </div>
        <ul className="space-y-2">
          {DAYS.map((d) => {
            const on = selected.includes(d.key);
            return (
              <li
                key={d.key}
                className="flex items-center justify-between py-1.5"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-bold ${
                      on ? "bg-brand-100 text-brand-700" : "bg-cream-100 text-ink-400"
                    }`}
                  >
                    {d.key}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-ink-900">{d.full}</div>
                    <div className="text-[10px] text-ink-500">
                      {on ? "Available all day" : "Not available"}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => toggle(d.key)}
                  className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${
                    on ? "bg-brand-500" : "bg-ink-200"
                  }`}
                  aria-pressed={on}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${
                      on ? "translate-x-5" : "translate-x-0"
                    }`}
                  ></span>
                </button>
              </li>
            );
          })}
        </ul>

        <button
          onClick={save}
          disabled={!dirty || saving}
          className={`w-full mt-4 py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-colors ${
            dirty
              ? "bg-brand-500 hover:bg-brand-600 text-white"
              : "bg-cream-100 text-ink-400 cursor-not-allowed"
          }`}
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-1.5"></i>
              Saving…
            </>
          ) : dirty ? (
            "Save Changes"
          ) : (
            "Saved"
          )}
        </button>
      </section>

      {/* 2-week preview */}
      <section className="bg-white border border-ink-100 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
            Next 2 weeks
          </div>
          <span className="text-[10px] font-semibold text-emerald-600">
            {twoWeeks.filter((d) => selected.includes(dayKeyForDate(d))).length} days available
          </span>
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {DAYS.map((d) => (
            <div
              key={d.key}
              className="text-[9px] font-bold uppercase text-center text-ink-400 tracking-wider"
            >
              {d.short.slice(0, 1)}
            </div>
          ))}
          {twoWeeks.map((d) => {
            const k = dayKeyForDate(d);
            const av = selected.includes(k);
            const today = isToday(d);
            return (
              <div
                key={d.toISOString()}
                className={`aspect-square rounded-lg text-[11px] font-bold flex items-center justify-center relative ${
                  av
                    ? "bg-brand-100 text-brand-800 border border-brand-200"
                    : "bg-cream-100 text-ink-400"
                } ${today ? "ring-2 ring-ink-900 ring-offset-1 ring-offset-white" : ""}`}
              >
                {d.getDate()}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mt-3 text-[10px] text-ink-500">
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm bg-brand-100 border border-brand-200"></span>
            Available
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm bg-cream-100"></span>
            Off
          </span>
        </div>
      </section>

      {/* Time off request */}
      <section className="bg-ink-900 text-white rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">
            <i className="ri-calendar-close-line text-base"></i>
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm font-bold">Need time off?</div>
            <p className="text-[11px] text-white/70 mt-0.5">
              Request specific dates and your manager will get notified.
            </p>
          </div>
          <button
            onClick={() => onToast("Request sent to dispatch")}
            className="px-3 py-2 rounded-lg bg-white text-ink-900 hover:bg-cream-100 text-[11px] font-bold cursor-pointer whitespace-nowrap"
          >
            Request
          </button>
        </div>
      </section>
    </div>
  );
}